import { useRef, useState } from 'react'
import { useStore } from '@tanstack/react-form'
import { FileMinus, Loader2 } from 'lucide-react'
import { Dialog, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { FormDialogContent } from '@/components/ui/form-dialog-content'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { getFieldError } from '@/components/clientes/wizard/useClienteForm'
import { useAcreditadoDeFactura } from '@/hooks/useFacturas'
import { formatCurrency } from '@/lib/utils'
import { etiquetaComprobante, type FacturaResumen } from '@/services/facturasService'
import { parsearImporte, useNotaCreditoParcialForm } from './forms'

export interface NotaCreditoParcial {
  importe: number
  concepto: string
  enviarEmail: boolean
  /** Id de la nota de crédito. Se repite mientras el diálogo siga abierto, así un reenvío no emite otra. */
  solicitudId: string
}

interface NotaCreditoParcialDialogProps {
  factura: FacturaResumen | null
  onOpenChange: (open: boolean) => void
  /** Rechaza si la function no la acepta: el diálogo queda abierto para corregir. */
  onEmitir: (nota: NotaCreditoParcial) => Promise<unknown>
}

const ErrorDeCampo = ({ errors }: { errors: unknown[] }) =>
  errors.length > 0 ? <p className="text-sm text-destructive">{getFieldError(errors)}</p> : null

/**
 * Nota de crédito por una parte de una factura autorizada (ORR-255 fase C). Se emite ante ARCA y no se
 * deshace, pero no anula la factura ni toca el cobro ni la cuenta corriente.
 */
export function NotaCreditoParcialDialog({ factura, onOpenChange, onEmitir }: NotaCreditoParcialDialogProps) {
  const acreditado = useAcreditadoDeFactura(factura?.id)
  const saldo = factura && acreditado.data !== undefined ? Math.round((Number(factura.importe_total) - acreditado.data) * 100) / 100 : null
  return (
    <Dialog open={!!factura} onOpenChange={onOpenChange}>
      {factura && (
        <>
          {saldo === null ? (
            <FormDialogContent isDirty={false} onDiscard={() => onOpenChange(false)} className="sm:max-w-[480px]">
              <DialogHeader>
                <DialogTitle>Nota de crédito parcial</DialogTitle>
                <DialogDescription>
                  {acreditado.isError ? 'No se pudo leer lo que ya se acreditó de esta factura.' : 'Leyendo lo que ya se acreditó de la factura...'}
                </DialogDescription>
              </DialogHeader>
              {!acreditado.isError && <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />}
            </FormDialogContent>
          ) : (
            // La validación del formulario se arma con el saldo leído: el formulario se monta cuando ya lo tiene.
            <Formulario factura={factura} saldo={saldo} onOpenChange={onOpenChange} onEmitir={onEmitir} />
          )}
        </>
      )}
    </Dialog>
  )
}

function Formulario({
  factura,
  saldo,
  onOpenChange,
  onEmitir,
}: { factura: FacturaResumen; saldo: number } & Pick<NotaCreditoParcialDialogProps, 'onOpenChange' | 'onEmitir'>) {
  const [emitiendo, setEmitiendo] = useState(false)
  const solicitudId = useRef<string | null>(null)
  const { form } = useNotaCreditoParcialForm({
    saldo,
    onSubmit: async (v) => {
      setEmitiendo(true)
      solicitudId.current ??= crypto.randomUUID()
      try {
        await onEmitir({
          importe: parsearImporte(v.importe),
          concepto: v.concepto.trim(),
          enviarEmail: v.enviarEmail && !!factura.receptor_email,
          solicitudId: solicitudId.current,
        })
        onOpenChange(false)
      } catch {
        // El error ya lo muestra la mutación. El diálogo queda abierto, con la misma solicitud: si la nota de
        // crédito se creó y se perdió la respuesta, reenviar devuelve esa y no emite otra.
      } finally {
        setEmitiendo(false)
      }
    },
  })
  const isDirty = useStore(form.store, (s) => s.isDirty)
  const etiqueta = etiquetaComprobante(factura) ?? 'la factura'

  return (
    <FormDialogContent isDirty={isDirty} onDiscard={() => onOpenChange(false)} className="sm:max-w-[480px]">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <FileMinus className="h-5 w-5" />
          Nota de crédito parcial
        </DialogTitle>
        <DialogDescription>
          Acredita una parte de la {etiqueta}
          {factura.receptor_nombre ? ` de ${factura.receptor_nombre}` : ''}. Se emite ante ARCA y no se puede borrar. No
          anula la factura ni cambia el cobro ni la cuenta corriente.
        </DialogDescription>
      </DialogHeader>

      {saldo <= 0 ? (
        <p className="text-sm text-muted-foreground">Las notas de crédito ya acreditan la factura entera.</p>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault()
            form.handleSubmit()
          }}
          className="space-y-4"
        >
          <p className="text-sm">
            Total de la factura {formatCurrency(Number(factura.importe_total))} · queda por acreditar{' '}
            <span className="font-semibold">{formatCurrency(saldo)}</span>
          </p>
          <form.Field name="importe">
            {(field) => (
              <div className="space-y-1.5">
                <Label htmlFor="nc-parcial-importe">Importe</Label>
                <Input
                  id="nc-parcial-importe"
                  inputMode="decimal"
                  placeholder="0,00"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                />
                <ErrorDeCampo errors={field.state.meta.errors} />
              </div>
            )}
          </form.Field>
          <form.Field name="concepto">
            {(field) => (
              <div className="space-y-1.5">
                <Label htmlFor="nc-parcial-concepto">Concepto</Label>
                <Input
                  id="nc-parcial-concepto"
                  placeholder="Por ejemplo: descuento por pronto pago"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                />
                <ErrorDeCampo errors={field.state.meta.errors} />
              </div>
            )}
          </form.Field>
          {factura.receptor_email && (
            <form.Field name="enviarEmail">
              {(field) => (
                <div className="flex items-center gap-2">
                  <Checkbox id="nc-parcial-enviar" checked={field.state.value} onCheckedChange={(v) => field.handleChange(v === true)} />
                  <label htmlFor="nc-parcial-enviar" className="text-sm">
                    Enviarla por mail a {factura.receptor_email}
                  </label>
                </div>
              )}
            </form.Field>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={emitiendo}>
              Cancelar
            </Button>
            <Button type="submit" disabled={emitiendo}>
              {emitiendo && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Emitir nota de crédito
            </Button>
          </DialogFooter>
        </form>
      )}
    </FormDialogContent>
  )
}
